import { FC, ReactNode, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import { RendererLoadingState } from './RendererLoadingState.component'
import { RendererErrorState } from './RendererErrorState.component'
import { UploadedFilesSection } from './UploadedFilesSection.component'
import { logger } from 'utils/logging'

interface Props {
  assignmentId: string
  isLoading: boolean
  isError: boolean
  onRetry?: () => void
  children: ReactNode
}

/**
 * Handles React Query state (loading / error) for assignment detail renderers.
 * Shows a loading spinner, an error fallback with uploaded files, or the
 * happy-path content wrapped in a `Stack`.
 *
 * @example
 * <RendererQueryState assignmentId={assignment.id} isLoading={isLoading} isError={isError}>
 *   <DetailSection title="...">...</DetailSection>
 *   <UploadedFilesSection assignmentId={assignment.id} />
 * </RendererQueryState>
 */
export const RendererQueryState: FC<Props> = ({ assignmentId, isLoading, isError, onRetry, children }) => {
  useEffect(() => {
    if (isError) {
      logger.warn('Failed to load assignment details', {
        context: {
          component: 'RendererQueryState',
          metadata: { assignmentId }
        }
      })
    }
  }, [isError, assignmentId])

  if (isLoading) return <RendererLoadingState />

  if (isError) return (
    <Stack gap="0.8rem">
      <RendererErrorState onRetry={onRetry} />
      <UploadedFilesSection assignmentId={assignmentId} />
    </Stack>
  )

  return (
    <Stack gap="0.8rem">
      {children}
    </Stack>
  )
}
